import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';

/**
 * Type alias for filter types used in Kendo UI data operations.
 * Represents either a simple filter descriptor or a composite filter descriptor.
 * 
 * @example
 * ```typescript
 * const simpleFilter: KendoFilter = { field: 'name', operator: 'contains', value: 'John' };
 * const compositeFilter: KendoFilter = { 
 *   logic: 'and', 
 *   filters: [
 *     { field: 'age', operator: 'gte', value: 18 },
 *     { field: 'status', operator: 'eq', value: 'active' }
 *   ] 
 * };
 * ```
 */
export type KendoFilter = FilterDescriptor | CompositeFilterDescriptor;

/**
 * Recursively searches for a filter by field name in a filter array.
 * 
 * This function will traverse through nested composite filters to find a match.
 * Returns the first matching filter found or null if no match exists.
 * 
 * @param filters The array of filters to search through.
 * @param field The field name to find in the filters.
 * @returns The found filter descriptor or null if not found.
 * 
 * @example
 * ```typescript
 * const filters = [
 *   { field: 'name', operator: 'contains', value: 'John' },
 *   { 
 *     logic: 'or', 
 *     filters: [
 *       { field: 'age', operator: 'gte', value: 18 },
 *       { field: 'status', operator: 'eq', value: 'active' }
 *     ] 
 *   }
 * ];
 * 
 * const nameFilter = findFilter(filters, 'name'); // Returns { field: 'name', operator: 'contains', value: 'John' }
 * const ageFilter = findFilter(filters, 'age');   // Returns { field: 'age', operator: 'gte', value: 18 }
 * const cityFilter = findFilter(filters, 'city'); // Returns null
 * ```
 */
export function findFilter(filters: KendoFilter[], field: string): FilterDescriptor | null {
  if (!filters || !filters.length) {
    return null;
  }

  for (const filter of filters) {
    if ('field' in filter && filter.field === field) {
      return filter;
    } else if ('filters' in filter) {
      const result = findFilter(filter.filters, field);
      if (result) {
        return result;
      }
    }
  }
  return null;
}

/**
 * Checks if a filter with the specified field name exists in the filter array.
 * 
 * This is a convenience function that uses findFilter internally and converts
 * the result to a boolean value.
 * 
 * @param filters The array of filters to search through.
 * @param field The field name to check for.
 * @returns True if a filter for the given field exists, false otherwise.
 * 
 * @example
 * ```typescript
 * const filters = [
 *   { field: 'name', operator: 'contains', value: 'John' },
 *   { field: 'age', operator: 'gte', value: 18 }
 * ];
 * 
 * const hasNameFilter = hasFilter(filters, 'name'); // Returns true
 * const hasCityFilter = hasFilter(filters, 'city'); // Returns false
 * ```
 */
export function hasFilter(filters: KendoFilter[], field: string): boolean {
  return findFilter(filters, field) !== null;
}

/**
 * Gets a filter value based on field name and converts it to a string.
 * 
 * This function retrieves the value of the first filter that matches the given field
 * and converts it to a string. If no filter is found, an empty string is returned.
 * 
 * @param filters The array of filters to search through.
 * @param field The field name to get the value for.
 * @returns The filter value as string or empty string if not found.
 * 
 * @example
 * ```typescript
 * const filters = [
 *   { field: 'name', operator: 'contains', value: 'John' },
 *   { field: 'age', operator: 'gte', value: 25 }
 * ];
 * 
 * const nameValue = getFilterValue(filters, 'name'); // Returns "John"
 * const ageValue = getFilterValue(filters, 'age');   // Returns "25"
 * const cityValue = getFilterValue(filters, 'city'); // Returns ""
 * ```
 */
export function getFilterValue(filters: KendoFilter[], field: string): string {
  const filter = findFilter(filters, field);
  return filter?.value as string ?? '';
}

/**
 * Gets a filter value with its type preserved.
 * 
 * This function retrieves the value of the first filter that matches the given field
 * and preserves its original type. If no filter is found, null is returned.
 * 
 * @template T The expected type of the filter value.
 * @param filters The array of filters to search through.
 * @param field The field name to get the value for.
 * @returns The filter value with its original type or null if not found.
 * 
 * @example
 * ```typescript
 * const filters = [
 *   { field: 'name', operator: 'contains', value: 'John' },
 *   { field: 'age', operator: 'gte', value: 25 },
 *   { field: 'isActive', operator: 'eq', value: true }
 * ];
 * 
 * const nameValue = getFilterValueTyped<string>(filters, 'name');     // Returns "John"
 * const ageValue = getFilterValueTyped<number>(filters, 'age');       // Returns 25
 * const isActive = getFilterValueTyped<boolean>(filters, 'isActive'); // Returns true
 * const city = getFilterValueTyped<string>(filters, 'city');          // Returns null
 * ```
 */
export function getFilterValueTyped<T = any>(filters: KendoFilter[], field: string): T | null {
  const filter = findFilter(filters, field);
  return filter?.value as T ?? null;
}

/**
 * Recursively adds or updates a filter in the filter array.
 * 
 * If a filter with the specified field already exists, it is updated with the new
 * value, operator, and ignoreCase settings. If no matching filter is found, a new
 * filter is added at the root level of the filter array.
 * 
 * @param filters The array of filters to modify.
 * @param field The field name to set the value for.
 * @param value The value to set.
 * @param operator The filter operator to use. Defaults to 'contains'.
 * @param ignoreCase Whether to ignore case sensitivity. Defaults to true.
 * @returns True if an existing filter was updated, false if a new filter was added.
 * 
 * @example
 * ```typescript
 * const filters = [
 *   { field: 'name', operator: 'contains', value: 'John' }
 * ];
 * 
 * // Update existing filter
 * setFilterValue(filters, 'name', 'Jane'); // Returns true, filters now has name = 'Jane'
 * 
 * // Add new filter
 * setFilterValue(filters, 'age', 30, 'gte'); // Returns false, adds new filter
 * // filters is now: [
 * //   { field: 'name', operator: 'contains', value: 'Jane' },
 * //   { field: 'age', operator: 'gte', value: 30, ignoreCase: true }
 * // ]
 * ```
 */
export function setFilterValue(
  filters: KendoFilter[], 
  field: string, 
  value: any, 
  operator: string = 'contains', 
  ignoreCase: boolean = true
): boolean {
  if (!filters) {
    return false;
  }

  // First attempt to update existing filter at any level
  const updated = updateFilterRecursively(filters, field, value, operator, ignoreCase);
  
  // If no existing filter was found, add a new one at the root level
  if (!updated) {
    filters.push({
      field,
      operator,
      value,
      ignoreCase
    });
  }
  
  return updated;
}

/**
 * Helper function to recursively update a filter within a filter structure.
 * 
 * @param filters The array of filters to modify.
 * @param field The field name to set the value for.
 * @param value The value to set.
 * @param operator The filter operator to use.
 * @param ignoreCase Whether to ignore case sensitivity.
 * @returns True if a filter was updated, false otherwise.
 * @internal
 */
function updateFilterRecursively(
  filters: KendoFilter[], 
  field: string, 
  value: any, 
  operator: string, 
  ignoreCase: boolean
): boolean {
  for (let i = 0; i < filters.length; i++) {
    const filter = filters[i];
    
    if ('field' in filter && filter.field === field) {
      filter.value = value;
      filter.operator = operator;
      filter.ignoreCase = ignoreCase;
      return true;
    } else if ('filters' in filter) {
      if (updateFilterRecursively(filter.filters, field, value, operator, ignoreCase)) {
        return true;
      }
    }
  }
  
  return false;
}

/**
 * Recursively removes a filter by field name from any level of the filter structure.
 * 
 * This function will also clean up empty composite filters after removing a filter.
 * If a filter is removed from a composite filter and that composite becomes empty,
 * the composite filter itself will be removed.
 * 
 * @param filters The array of filters to modify.
 * @param field The field name to remove.
 * @returns True if a filter was removed, false otherwise.
 * 
 * @example
 * ```typescript
 * const filters = [
 *   { field: 'name', operator: 'contains', value: 'John' },
 *   { 
 *     logic: 'or', 
 *     filters: [
 *       { field: 'age', operator: 'gte', value: 18 }
 *     ] 
 *   }
 * ];
 * 
 * removeFilter(filters, 'name'); // Returns true, removes name filter
 * removeFilter(filters, 'age');  // Returns true, removes age filter and its parent composite
 * removeFilter(filters, 'city'); // Returns false, no filter to remove
 * ```
 */
export function removeFilter(filters: KendoFilter[], field: string): boolean {
  if (!filters || !filters.length) {
    return false;
  }

  // First try to remove from top level
  const index = filters.findIndex(f => 'field' in f && f.field === field);
  if (index >= 0) {
    filters.splice(index, 1);
    return true;
  }

  // Then try to remove from nested filters
  for (const filter of filters) {
    if ('filters' in filter) {
      if (removeFilter(filter.filters, field)) {
        // Clean up empty composite filters
        if (filter.filters.length === 0) {
          const compositeIndex = filters.indexOf(filter);
          if (compositeIndex >= 0) {
            filters.splice(compositeIndex, 1);
          }
        }
        return true;
      }
    }
  }

  return false;
}

/**
 * Creates a deep clone of filter objects to avoid mutation issues.
 * 
 * This function creates a completely new copy of the filter structure,
 * ensuring that any modifications to the clone do not affect the original.
 * 
 * @param filters The filters to clone.
 * @returns A deep clone of the provided filters.
 * 
 * @example
 * ```typescript
 * const original = [
 *   { field: 'name', operator: 'contains', value: 'John' },
 *   { 
 *     logic: 'and', 
 *     filters: [
 *       { field: 'age', operator: 'gte', value: 18 }
 *     ] 
 *   }
 * ];
 * 
 * const clone = cloneFilters(original);
 * // Modifying clone won't affect original
 * clone[0].value = 'Jane';
 * ```
 */
export function cloneFilters(filters: KendoFilter[]): KendoFilter[] {
  if (!filters) {
    return [];
  }
  return JSON.parse(JSON.stringify(filters));
}

/**
 * Clears all filters by emptying the filter array.
 * 
 * This function modifies the original array in-place, removing all filters.
 * 
 * @param filters The array of filters to clear.
 * 
 * @example
 * ```typescript
 * const filters = [
 *   { field: 'name', operator: 'contains', value: 'John' },
 *   { field: 'age', operator: 'gte', value: 18 }
 * ];
 * 
 * clearFilters(filters); // filters is now an empty array []
 * ```
 */
export function clearFilters(filters: KendoFilter[]): void {
  if (filters) {
    filters.length = 0;
  }
}

/**
 * Creates a composite filter with the specified operator.
 * 
 * A composite filter contains multiple filter conditions combined with 
 * a logical operator (AND/OR).
 * 
 * @param operator The logical operator ('and' or 'or'). Defaults to 'and'.
 * @param filters The filters to include in the composite. Defaults to an empty array.
 * @returns A new composite filter descriptor.
 * 
 * @example
 * ```typescript
 * // Create an empty AND composite filter
 * const emptyAnd = createCompositeFilter();
 * 
 * // Create an OR composite with existing filters
 * const orFilter = createCompositeFilter('or', [
 *   { field: 'category', operator: 'eq', value: 'Electronics' },
 *   { field: 'category', operator: 'eq', value: 'Computers' }
 * ]);
 * ```
 */
export function createCompositeFilter(
  operator: 'and' | 'or' = 'and', 
  filters: KendoFilter[] = []
): CompositeFilterDescriptor {
  return {
    logic: operator,
    filters
  };
}

/**
 * Adds a filter to a specific composite filter identified by a path.
 * 
 * This function navigates through the filter structure using the provided path
 * (array of indices) to find the target composite filter, then adds a new filter
 * to that composite.
 * 
 * @param filters The root filters array.
 * @param compositePath An array of indices indicating the path to the target composite filter.
 * @param field The field name for the new filter.
 * @param value The value for the new filter.
 * @param operator The filter operator to use. Defaults to 'contains'.
 * @returns True if the filter was added successfully, false otherwise.
 * 
 * @example
 * ```typescript
 * const filters = [
 *   { 
 *     logic: 'and', 
 *     filters: [
 *       { field: 'name', operator: 'contains', value: 'John' }
 *     ] 
 *   },
 *   {
 *     logic: 'or',
 *     filters: []
 *   }
 * ];
 * 
 * // Add a filter to the first composite (at index 0)
 * addFilterToComposite(filters, [0], 'age', 18, 'gte'); // Returns true
 * 
 * // Add a filter to the second composite (at index 1)
 * addFilterToComposite(filters, [1], 'status', 'active'); // Returns true
 * ```
 */
export function addFilterToComposite(
  filters: KendoFilter[],
  compositePath: number[],
  field: string,
  value: any,
  operator: string = 'contains'
): boolean {
  if (!filters || !compositePath.length) {
    return false;
  }

  let target: KendoFilter[] = filters;
  let current: KendoFilter | undefined;

  // Navigate to the target composite filter
  for (const idx of compositePath) {
    if (idx < 0 || idx >= target.length) {
      return false;
    }
    
    current = target[idx];
    if (!('filters' in current)) {
      return false;
    }
    
    target = current.filters;
  }

  // Add the new filter to the target
  target.push({
    field,
    operator,
    value,
    ignoreCase: true
  });

  return true;
}

/**
 * Creates a date range filter for a field.
 * 
 * This function creates a composite filter with two conditions:
 * 1. A "greater than or equal to" condition for the start date
 * 2. A "less than or equal to" condition for the end date
 * 
 * Together, these define an inclusive date range.
 * 
 * @param field The field name to filter on.
 * @param fromDate The start date (inclusive).
 * @param toDate The end date (inclusive).
 * @returns A composite filter with the date range.
 * 
 * @example
 * ```typescript
 * const startDate = new Date('2023-01-01');
 * const endDate = new Date('2023-12-31');
 * 
 * const dateFilter = createDateRangeFilter('orderDate', startDate, endDate);
 * // Returns a composite filter that selects all orders between Jan 1 and Dec 31, 2023
 * ```
 */
export function createDateRangeFilter(
  field: string, 
  fromDate: Date, 
  toDate: Date
): CompositeFilterDescriptor {
  return {
    logic: 'and',
    filters: [
      {
        field,
        operator: 'gte',
        value: fromDate
      },
      {
        field,
        operator: 'lte',
        value: toDate
      }
    ]
  };
}

/**
 * Gets all field names used in the filter structure.
 * 
 * This function recursively extracts all field names from all filters,
 * including those in nested composite filters, and returns them as a
 * unique array of strings.
 * 
 * @param filters The filters to extract field names from.
 * @returns An array of unique field names.
 * 
 * @example
 * ```typescript
 * const filters = [
 *   { field: 'name', operator: 'contains', value: 'John' },
 *   { 
 *     logic: 'and', 
 *     filters: [
 *       { field: 'age', operator: 'gte', value: 18 },
 *       { field: 'name', operator: 'contains', value: 'Doe' }
 *     ] 
 *   }
 * ];
 * 
 * const fields = getFilterFields(filters); // Returns ['name', 'age']
 * ```
 */
export function getFilterFields(filters: KendoFilter[]): string[] {
  const fields = new Set<string>();
  
  function extractFields(filterArray: KendoFilter[]) {
    if (!filterArray) return;
    
    for (const filter of filterArray) {
      if ('field' in filter && typeof filter.field === 'string') {
        fields.add(filter.field);
      } else if ('filters' in filter) {
        extractFields(filter.filters);
      }
    }
  }
  
  extractFields(filters);
  return Array.from(fields);
}

/**
 * Merges multiple filter arrays into a single array with the specified logic.
 * 
 * This function takes multiple arrays of filters and combines them into a single
 * composite filter with the specified logic. It handles edge cases like empty arrays
 * and creates deep clones of filters to avoid mutation issues.
 * 
 * @param logic The logic to apply ('and' or 'or'). Defaults to 'and'.
 * @param filterArrays Arrays of filters to merge.
 * @returns A merged filter array.
 * 
 * @example
 * ```typescript
 * const nameFilters = [
 *   { field: 'firstName', operator: 'contains', value: 'John' },
 *   { field: 'lastName', operator: 'contains', value: 'Doe' }
 * ];
 * 
 * const ageFilter = [
 *   { field: 'age', operator: 'gte', value: 18 }
 * ];
 * 
 * // Merge with AND logic
 * const andMerged = mergeFilters('and', nameFilters, ageFilter);
 * // Results in a filter requiring both name conditions AND age >= 18
 * 
 * // Merge with OR logic
 * const orMerged = mergeFilters('or', nameFilters, ageFilter);
 * // Results in a filter requiring any of the name conditions OR age >= 18
 * ```
 */
export function mergeFilters(
  logic: 'and' | 'or' = 'and',
  ...filterArrays: KendoFilter[][]
): KendoFilter[] {
  if (!filterArrays.length) {
    return [];
  }
  
  if (filterArrays.length === 1) {
    return cloneFilters(filterArrays[0] || []);
  }
  
  const nonEmptyArrays = filterArrays.filter(arr => arr && arr.length > 0);
  
  if (nonEmptyArrays.length === 0) {
    return [];
  }
  
  if (nonEmptyArrays.length === 1) {
    return cloneFilters(nonEmptyArrays[0]);
  }
  
  // Ensure logic is always a string, using 'and' as default
  const filterLogic: 'and' | 'or' = logic || 'and';
  
  return [
    {
      logic: filterLogic,
      filters: nonEmptyArrays.flatMap(arr => cloneFilters(arr))
    }
  ];
}

/**
 * Creates a simple filter descriptor.
 * 
 * This is a convenience function to create a simple filter with standard defaults.
 * 
 * @param field The field name to filter on.
 * @param value The filter value.
 * @param operator The filter operator. Defaults to 'contains'.
 * @param ignoreCase Whether to ignore case sensitivity. Defaults to true.
 * @returns A new filter descriptor.
 * 
 * @example
 * ```typescript
 * // Create a simple text contains filter
 * const nameFilter = createFilter('name', 'John');
 * 
 * // Create an equals filter
 * const statusFilter = createFilter('status', 'active', 'eq');
 * 
 * // Create a case-sensitive starts with filter
 * const codeFilter = createFilter('code', 'ABC', 'startswith', false);
 * ```
 */
export function createFilter(
  field: string,
  value: any,
  operator: string = 'contains',
  ignoreCase: boolean = true
): FilterDescriptor {
  return {
    field,
    operator,
    value,
    ignoreCase
  };
}

/**
 * Creates filters for multiple fields with the same value pattern.
 * 
 * This function creates a composite OR filter that matches the given value
 * across multiple fields. This is useful for implementing search functionality
 * that looks for a term across several different fields.
 * 
 * @param fields Array of field names to filter on.
 * @param value The value to search for in all fields.
 * @param operator The filter operator. Defaults to 'contains'.
 * @returns A composite filter with 'or' logic containing all field filters.
 * 
 * @example
 * ```typescript
 * // Search for "Smith" in multiple name fields
 * const nameSearch = createMultiFieldFilter(
 *   ['firstName', 'lastName', 'middleName'],
 *   'Smith'
 * );
 * 
 * // Search for exact code match across multiple code fields
 * const codeSearch = createMultiFieldFilter(
 *   ['productCode', 'skuCode', 'barcodeNumber'],
 *   'ABC123',
 *   'eq'
 * );
 * ```
 */
export function createMultiFieldFilter(
  fields: string[],
  value: any,
  operator: string = 'contains'
): CompositeFilterDescriptor {
  return {
    logic: 'or',
    filters: fields.map(field => ({
      field,
      operator,
      value,
      ignoreCase: true
    }))
  };
}